import { CircleCheck } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const leagueCreatedDialogColors = {
  media: "size-8 bg-primary/10",
  icon: "size-4 text-primary",
  dashboard: "hover:text-primary hover:border-primary/30 rounded-lg",
  open: "active:scale-[0.97] rounded-lg",
};

type LeagueCreatedDialogVariant = "created" | "draft" | "finalized";

/**
 * Three endings, because the operator is not always in the same place:
 *   - "draft" is Guided Setup, which stops at a draft — so the window says the
 *     league is one and names the next step rather than only reporting success.
 *   - "created" is the advanced editor finishing a brand-new league, which is
 *     already finalized by then and needs no instructions.
 *   - "finalized" is the advanced editor finalizing a league that existed.
 */
const DIALOG_COPY: Record<
  LeagueCreatedDialogVariant,
  { title: string; named: (name: string) => string; unnamed: string }
> = {
  created: {
    title: "League created",
    named: (name) =>
      `${name} was created successfully. Open it now or go back to the dashboard.`,
    unnamed:
      "The league was created successfully. Open it now or go back to the dashboard.",
  },
  draft: {
    title: "League created",
    named: (name) =>
      `${name} was created as a draft. Open the league to add players and finalize it.`,
    unnamed:
      "The league was created as a draft. Open the league to add players and finalize it.",
  },
  finalized: {
    title: "League finalized",
    named: (name) => `${name} was finalized successfully.`,
    unnamed: "The league was finalized successfully.",
  },
};

interface LeagueCreatedDialogProps {
  open: boolean;
  leagueName?: string;
  variant?: LeagueCreatedDialogVariant;
  onGoToDashboard: () => void;
  onOpenLeague: () => void;
}

export function LeagueCreatedDialog({
  open,
  leagueName,
  variant = "created",
  onGoToDashboard,
  onOpenLeague,
}: LeagueCreatedDialogProps) {
  const name = leagueName?.trim();
  const copy = DIALOG_COPY[variant];

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) return;
      }}
    >
      <AlertDialogContent size="sm">
        <AlertDialogHeader>
          <AlertDialogMedia className={leagueCreatedDialogColors.media}>
            <CircleCheck className={leagueCreatedDialogColors.icon} />
          </AlertDialogMedia>
          <AlertDialogTitle>{copy.title}</AlertDialogTitle>
          <AlertDialogDescription>
            {name ? copy.named(name) : copy.unnamed}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-3">
          <AlertDialogCancel
            onClick={onGoToDashboard}
            className={leagueCreatedDialogColors.dashboard}
          >
            Go to Dashboard
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onOpenLeague}
            className={leagueCreatedDialogColors.open}
          >
            Open league
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
